import type { ThemeColorName } from '@examdojo/styles';

/**
 * Variant options for label styling.
 * - 'primary': white text on feature color 500 background
 * - 'secondary': feature color 600 text on feature color 100 background (default)
 * - 'tertiary': feature color 700 text, no background
 */
export type LabelVariant = 'primary' | 'secondary' | 'tertiary';

/**
 * Result of label style computation.
 */
export interface LabelStyleResult {
  backgroundColor: string;
  textColor: string;
}

/**
 * Label position relative to the element.
 *
 * For **points**: Positions are relative to the point location.
 *
 * For **lines/segments**: The label is placed at the midpoint.
 * - `left`/`right`: Perpendicular to the line direction, relative to the
 *   outer set points (start → end). `left` is on the left side when looking
 *   from start to end, `right` is on the right side.
 * - `top`/`bottom`: Above or below the midpoint (in screen coordinates).
 *
 * Use the `offset` property in `ElementLabelConfig` for fine-tuning placement.
 */
export type LabelPosition =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'topLeft'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomRight'
  | 'center';

/**
 * Content variant for label text generation.
 * Provides convenient shortcuts for common label formats.
 */
export type LabelContentVariant =
  | 'name' // Just the name: "A"
  | 'coord' // Just coordinates (points): "(2.00, 3.00)"
  | 'name+coord' // Name and coordinates (points): "A (2.00, 3.00)"
  | 'deg' // Just degrees (angles): "45.0°"
  | 'name+deg' // Name and degrees (angles): "∠ABC = 45.0°"
  | 'length' // Just length (lines): "5.00"
  | 'name+length'; // Name and length (lines): "Δx = 5.00"

/**
 * Context for point labels.
 */
export interface PointLabelContext {
  type: 'point';
  name: string;
  x: () => number;
  y: () => number;
}

/**
 * Context for line labels.
 */
export interface LineLabelContext {
  type: 'line';
  name: string;
  length: () => number;
}

/**
 * Context for polygon labels.
 */
export interface PolygonLabelContext {
  type: 'polygon';
  name: string;
  area: () => number;
  perimeter: () => number;
}

/**
 * Context for curve labels.
 */
export interface CurveLabelContext {
  type: 'curve';
  name: string;
}

/**
 * Context for angle labels.
 */
export interface AngleLabelContext {
  type: 'angle';
  name: string;
  degrees: () => number;
  radians: () => number;
}

/**
 * Discriminated union of all label contexts.
 */
export type LabelContext =
  | PointLabelContext
  | LineLabelContext
  | PolygonLabelContext
  | CurveLabelContext
  | AngleLabelContext;

/**
 * Extended label configuration for elements.
 */
export interface ElementLabelConfig {
  show?: boolean;
  variant?: LabelVariant;
  color?: ThemeColorName;
  /** Position of the label relative to the element */
  position?: LabelPosition;
  /** Content variant for automatic text generation */
  contentVariant?: LabelContentVariant;
  /** Custom format function for complex programmatic logic (overrides contentVariant) */
  formatFn?: (context: LabelContext) => string;
  /**
   * Label text with optional template placeholders for dynamic values.
   * Overrides both contentVariant and formatFn.
   *
   * If text contains `{placeholder}` syntax, values are interpolated dynamically.
   * Otherwise, text is displayed as-is (static).
   *
   * Available placeholders depend on element type:
   * - Points: `{name}`, `{x}`, `{y}`
   * - Lines: `{name}`, `{length}`
   * - Angles: `{name}`, `{deg}`, `{rad}`
   * - Polygons: `{name}`, `{area}`, `{perimeter}`
   *
   * @example "Hello" → "Hello" (static text)
   * @example "Δx = {length}" → "Δx = 5.00" (template)
   * @example "{name} at ({x}, {y})" → "A at (2.00, 3.00)" (template)
   */
  text?: string;
  /**
   * Additional pixel offset from the computed position [dx, dy].
   * This is added to the position-based offset for fine-tuning label placement.
   * Positive dx moves right, positive dy moves up.
   * @example [10, -5] // Move 10px right and 5px down from computed position
   */
  offset?: [number, number];
}

/**
 * Resolved label configuration with all required fields.
 */
export interface ResolvedLabelConfig {
  show: boolean;
  variant: LabelVariant;
  color: ThemeColorName;
  position: LabelPosition;
  contentVariant: LabelContentVariant;
  formatFn?: (context: LabelContext) => string;
  text?: string;
  offset: [number, number];
}

/**
 * Normalize a label config input to a resolved config.
 */
export function resolveLabelConfig(
  config: ElementLabelConfig | boolean | undefined,
  defaults: Partial<ResolvedLabelConfig> = {},
): ResolvedLabelConfig | null {
  if (config === false || config === undefined) {
    return null;
  }

  if (config === true) {
    return {
      show: true,
      variant: defaults.variant ?? 'secondary',
      color: defaults.color ?? 'neutral',
      position: defaults.position ?? 'topRight',
      contentVariant: defaults.contentVariant ?? 'name',
      offset: defaults.offset ?? [0, 0],
    };
  }

  if (config.show === false) {
    return null;
  }

  return {
    show: config.show ?? true,
    variant: config.variant ?? defaults.variant ?? 'secondary',
    color: config.color ?? defaults.color ?? 'neutral',
    position: config.position ?? defaults.position ?? 'topRight',
    contentVariant: config.contentVariant ?? defaults.contentVariant ?? 'name',
    formatFn: config.formatFn,
    text: config.text,
    offset: config.offset ?? defaults.offset ?? [0, 0],
  };
}
